import { InlineMessage, Loader } from "@saviynt/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSsoConfig, useSpMetadata, useUpdateSso } from "../hooks";
import { ssoQueryKeys } from "../queryKeys";
import { parseZodErrors } from "../utils";
import PreCheckDialog from "./PreCheckDialog";
import SsoConfigForm from "./SsoConfigForm";

export default function SsoEditPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configFetchError,
    refetch: refetchConfig,
  } = useSsoConfig();
  const { data: spMetadata } = useSpMetadata();
  const updateSso = useUpdateSso();
  const [showEnableDialog, setShowEnableDialog] = useState(false);

  // Track whether the last mutation action was 'save' or 'enable'
  // to prevent duplicate error display (Bug 2 fix)
  const lastActionRef = useRef<"save" | "enable">("save");

  const handleBack = () => {
    navigate({ to: "/admin/settings/sso-settings/" });
  };

  const handleEnableSso = () => {
    setShowEnableDialog(true);
  };

  const handleConfirmEnable = () => {
    lastActionRef.current = "enable";
    updateSso.mutate(
      { status: "ENABLED" },
      {
        onSuccess: () => {
          setShowEnableDialog(false);
          queryClient.invalidateQueries({ queryKey: ssoQueryKeys.config });
        },
      },
    );
  };

  const handleSave = (values: {
    displayName: string;
    entityDescriptorUrl: string;
    emailAttribute: string;
  }) => {
    if (!config) return;

    const patch: Record<string, string> = {};
    if (values.displayName !== (config.displayName ?? "")) {
      patch.displayName = values.displayName;
    }
    if (values.entityDescriptorUrl !== (config.metadataUrl ?? "")) {
      patch.metadataUrl = values.entityDescriptorUrl;
    }
    if (values.emailAttribute !== (config.emailAttribute ?? "")) {
      patch.emailAttribute = values.emailAttribute;
    }

    if (Object.keys(patch).length === 0) {
      navigate({ to: "/admin/settings/sso-settings/" });
      return;
    }

    lastActionRef.current = "save";
    updateSso.mutate(patch, {
      onSuccess: () => {
        navigate({
          to: "/admin/settings/sso-settings/",
          search: { success: "updated" },
        });
      },
    });
  };

  if (isConfigLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader kind="loop" format="main" size="medium" color="brand" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6">
        <InlineMessage
          colorTheme="critical"
          text={intl.formatMessage({
            id: "kRZzRS",
            defaultMessage: "SSO configuration not found.",
            description: "Error when SSO config not found on edit page",
          })}
        />
      </div>
    );
  }

  // Parse field-specific validation errors from API response
  const fieldErrors =
    lastActionRef.current === "save" ? parseZodErrors(updateSso.error) : null;

  // Only show top-level error message when the last action was 'save'
  const errorMessage =
    updateSso.error && lastActionRef.current === "save"
      ? fieldErrors
        ? null
        : updateSso.error instanceof Error &&
            "status" in updateSso.error &&
            (updateSso.error as { status: number }).status === 404
          ? intl.formatMessage({
              id: "kRZzRS",
              defaultMessage: "SSO configuration not found.",
              description: "Error when SSO config not found on edit page",
            })
          : updateSso.error instanceof Error &&
              "message" in updateSso.error &&
              (updateSso.error.message === "Failed to fetch" ||
                updateSso.error.message === "Network Error" ||
                updateSso.error.message.toLowerCase().includes("network"))
            ? intl.formatMessage({
                id: "djfymb",
                defaultMessage:
                  "Unable to reach the server. Please check your connection.",
                description:
                  "Error when network request fails during SSO test connection",
              })
            : updateSso.error instanceof Error
              ? updateSso.error.message
              : intl.formatMessage({
                  id: "eVXW9F",
                  defaultMessage:
                    "Failed to update SSO settings. Please try again.",
                  description: "Generic SSO update error message",
                })
      : null;

  // Enable SSO error message — only show when the last action was 'enable'
  const enableErrorMessage =
    updateSso.error && lastActionRef.current === "enable"
      ? updateSso.error instanceof Error &&
        "status" in updateSso.error &&
        (updateSso.error as { status: number }).status === 404
        ? intl.formatMessage({
            id: "kRZzRS",
            defaultMessage: "SSO configuration not found.",
            description: "Error when SSO config not found on edit page",
          })
        : updateSso.error instanceof Error &&
            "message" in updateSso.error &&
            (updateSso.error.message === "Failed to fetch" ||
              updateSso.error.message === "Network Error" ||
              updateSso.error.message.toLowerCase().includes("network"))
          ? intl.formatMessage({
              id: "djfymb",
              defaultMessage:
                "Unable to reach the server. Please check your connection.",
              description:
                "Error when network request fails during SSO test connection",
            })
          : intl.formatMessage({
              id: "eVXW9F",
              defaultMessage:
                "Failed to update SSO settings. Please try again.",
              description: "Generic SSO update error message",
            })
      : null;

  const handleRetryConfigFetch = () => {
    refetchConfig();
  };

  return (
    <>
      <SsoConfigForm
        title={
          <FormattedMessage
            id="942/hm"
            defaultMessage="Edit SAML Provider"
            description="Edit SAML provider page heading"
          />
        }
        initialDisplayName={config.displayName ?? ""}
        initialEntityDescriptorUrl={config.metadataUrl ?? ""}
        initialEmailAttribute={config.emailAttribute ?? ""}
        spMetadata={
          spMetadata?.saml
            ? {
                entityId: spMetadata.saml.entityId,
                acsUrl: spMetadata.saml.acsUrl,
                sloUrl: spMetadata.saml.sloUrl,
              }
            : null
        }
        isPending={updateSso.isPending}
        error={errorMessage}
        fieldErrors={fieldErrors}
        onSave={handleSave}
        onBack={handleBack}
        ssoConfig={
          config
            ? {
                status: config.status,
                idpName: config.idpName,
                signingCert: config.signingCert,
                encryptionCert: config.encryptionCert,
              }
            : null
        }
        onEnableSso={handleEnableSso}
        isEnabling={updateSso.isPending}
        enableError={enableErrorMessage}
        configFetchError={
          configFetchError
            ? intl.formatMessage({
                id: "S9RO3p",
                defaultMessage:
                  "Unable to load certificate information. Please try again.",
                description:
                  "Error when SSO config refetch fails after save, shown in certificate section",
              })
            : null
        }
        onRetryConfigFetch={handleRetryConfigFetch}
      />

      <PreCheckDialog
        isOpen={showEnableDialog}
        onConfirm={handleConfirmEnable}
        onCancel={() => setShowEnableDialog(false)}
        isPending={updateSso.isPending}
      />
    </>
  );
}
